import React, { useEffect, useState } from 'react';
import {
  BackdropStyled,
  DialogBaseStyled,
  DialogContainerStyled,
  DialogContentStyled,
  DialogMainStyled,
} from '@nike/nike-design-system-components';
import { RemoteComponent } from '@nike/remote-component';
import { OverflowContainer, useQueryParamValue } from '@nike/privacy-shared-react';
import {
  RELIC_EVENT_NAME,
  addRelicAnalyticsPageAction,
  getRelicUserType,
} from '@nike/privacy-shared-logic';
import { Global } from '@emotion/core';

import { analyticsTrackPageViewedCookieDialog } from '../../helpers/analyticsHandlers';
import { usePrivacyConsentContext } from '../PrivacyConsentContextProvider';
import { CookieDialogActions } from './components/CookieDialogActions';
import { CookieDialogStyled } from './CookieDialogStyled';
import {
  INLINE_COOKIE_ENDPOINT,
  INLINE_COOKIE_STAGING_ENDPOINT,
  PRIVACY_COMPONENT_TYPE,
  COOKIE_DIALOG_BASE_CLASS_NAME,
} from '../../constants';

/**
 * @name CookieDialog
 * @description Component which renders a cookie dialog
 * @returns {JSX.Element|null}
 * @memberof mfe-privacy-consent
 */
export const CookieDialog = (): JSX.Element | null => {
  const {
    promptIsRequired,
    inlineCookieIsLoading,
    isLoading,
    isLearnMoreShown,
    contextFrameName,
    footer,
    isLoggedIn,
  } = usePrivacyConsentContext();
  const isStaging = useQueryParamValue('nikeEnv', 'staging');

  const [modalHeight, setModalHeight] = useState(0);

  useEffect(() => {
    if (!inlineCookieIsLoading && promptIsRequired) {
      analyticsTrackPageViewedCookieDialog();
      addRelicAnalyticsPageAction(RELIC_EVENT_NAME.PRIVACY_SETTINGS_VIEWED, {
        view_type: PRIVACY_COMPONENT_TYPE.MODAL,
        user_type: getRelicUserType(isLoggedIn),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inlineCookieIsLoading, promptIsRequired]);

  // It's necessary to get the modal height.
  // Without setTimeout returns default height without content height
  useEffect(() => {
    let timeoutID: ReturnType<typeof setTimeout> | undefined;

    if (!inlineCookieIsLoading && promptIsRequired) {
      timeoutID = setTimeout(() => {
        const modal = document.querySelector(`.${COOKIE_DIALOG_BASE_CLASS_NAME}`);

        modal && setModalHeight(modal.clientHeight);
      }, 10);
    }

    return () => {
      if (timeoutID) clearTimeout(timeoutID);
    };
  }, [inlineCookieIsLoading, promptIsRequired]);

  return (
    <OverflowContainer shouldBlockScroll={promptIsRequired && !inlineCookieIsLoading}>
      {/* This code needed to avoid scroll bug when we have error with loading remote component.
       Should be removed since `nds-no-scroll` will be removed from `Dialog` component */}
      {promptIsRequired && inlineCookieIsLoading && (
        <Global styles={{ body: { overflow: 'auto !important' } }} />
      )}
      <CookieDialogStyled
        className={COOKIE_DIALOG_BASE_CLASS_NAME}
        height={modalHeight}
        // passing an empty string for title prop, because it's hidden in the styles of CookieDialogStyled
        title=""
        isOpen={promptIsRequired}
        shouldCloseOnEsc={false}
        shouldCloseOnBackgroundClick={false}
        dialogRootCompProps={{
          'data-testid': 'cookie-dialog-root',
        }}
        dialogContainerCompProps={{
          'data-testid': 'cookie-dialog',
        }}
        dialogBaseCompProps={{
          // Using it to disable FocusTrap only in test environment because it has bug error with tabbable elements while we hide all containers
          trapFocus: process.env.NODE_ENV !== 'test',
        }}
        backdropCompProps={{
          style: {
            // It hides the modal during the loading and initializing process of RemoteComponent.
            visibility: inlineCookieIsLoading ? 'hidden' : 'visible',
            // The cookie modal should overlap geo-mismatch modal window when it is loaded
            zIndex: '2100',
          },
          'data-testid': 'dialog-backdrop',
        }}
        isLoading={isLoading}
        isLearnMoreShown={isLearnMoreShown}
        hasCookieDialogActions={!!footer.acceptAllButton || !!footer.declineAllButton}
        slots={{
          FooterSlot: <CookieDialogActions />,
        }}
        classes={{
          backdropContainer: 'dialog-backdrop-container',
          main: 'dialog-main',
          base: 'dialog-base',
          content: 'dialog-content',
        }}
        components={{
          DialogContentComp: DialogContentStyled,
          DialogMainComp: DialogMainStyled,
          DialogContainerComp: DialogContainerStyled,
          DialogBaseComp: DialogBaseStyled,
          BackdropComp: BackdropStyled,
        }}
      >
        {typeof document !== `undefined` && (
          <RemoteComponent
            contextFrameName={contextFrameName}
            endpoint={isStaging ? INLINE_COOKIE_STAGING_ENDPOINT : INLINE_COOKIE_ENDPOINT}
            prefetch={false}
          />
        )}
      </CookieDialogStyled>
    </OverflowContainer>
  );
};
